/**
 * format.js — display formatting.
 *
 * Two recurring bugs in the original are fixed here:
 *   1. timeAgo(ts) assumed a millisecond number. Firestore returns a Timestamp
 *      object, and an optimistic local write has `null` until the server
 *      resolves serverTimestamp(). Both produced "NaNd ago". toMillis()
 *      handles every shape.
 *   2. Relative times were the only label. A screen reader announcing "2h ago"
 *      is fine; a clinician checking whether a guideline update predates their
 *      last audit needs the real date. Every relative time now carries an
 *      absolute one in a title/aria-label.
 */

/** Coerce Firestore Timestamp | Date | number | null into milliseconds. */
export function toMillis(value) {
  if (value == null) return null;
  if (typeof value === 'number') return value;
  if (value instanceof Date) return value.getTime();
  if (typeof value.toMillis === 'function') return value.toMillis();      // Firestore Timestamp
  if (typeof value.seconds === 'number') return value.seconds * 1000;      // plain JSON Timestamp
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

/**
 * Compact relative time: "now", "4m", "3h", "2d", then a date.
 * Returns null for pending writes so the UI can show "Sending…" instead of a
 * bogus timestamp.
 */
export function timeAgo(value) {
  const ms = toMillis(value);
  if (ms == null) return null;

  const diff = Date.now() - ms;
  if (diff < 0) return 'now';                     // clock skew, do not show "in 3 seconds"
  const sec = Math.floor(diff / 1000);
  if (sec < 45) return 'now';
  if (sec < 3600) return `${Math.floor(sec / 60)}m`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h`;
  if (sec < 604800) return `${Math.floor(sec / 86400)}d`;

  const d = new Date(ms);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/** The full, unambiguous timestamp for tooltips and aria-labels. */
export function fullDate(value) {
  const ms = toMillis(value);
  if (ms == null) return '';
  return new Date(ms).toLocaleString(undefined, {
    weekday: 'short', day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

/** Spoken form for screen readers: "3 hours ago", not "3h". */
export function spokenAgo(value) {
  const ms = toMillis(value);
  if (ms == null) return 'just now';
  const sec = Math.round((ms - Date.now()) / 1000);
  const units = [
    ['year', 31536000], ['month', 2592000], ['week', 604800],
    ['day', 86400], ['hour', 3600], ['minute', 60],
  ];
  for (const [unit, secs] of units) {
    if (Math.abs(sec) >= secs) return rtf.format(Math.round(sec / secs), unit);
  }
  return 'just now';
}

/** 1 → "1", 1200 → "1.2K", 1_500_000 → "1.5M". */
export function compactNumber(n) {
  const value = Number(n) || 0;
  if (value < 1000) return String(value);
  return new Intl.NumberFormat(undefined, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

/** Correct plural without the "1 comments" bug. */
export function plural(count, singular, pluralForm) {
  const n = Number(count) || 0;
  return `${compactNumber(n)} ${n === 1 ? singular : pluralForm ?? `${singular}s`}`;
}

/**
 * Initials for the avatar fallback.
 * Handles single names, extra whitespace, and emoji-only display names without
 * producing "?" or slicing a surrogate pair in half (which renders as "�").
 */
// Honorifics are stripped before deriving initials, before picking a given
// name, and before building search tokens. Without this, "Dr Amara Rivera"
// produces the monogram "DR" — the avatar shows the title, not the person, and
// every doctor on the platform gets an identical one.
//
// One list, used by initials(), givenName() and searchTokens() below. Keep it
// that way: three copies drift, and a title stripped in one place but not
// another is exactly how "Replying to Mrs" happens.
const HONORIFICS = new Set([
  'dr', 'dr.', 'doctor', 'prof', 'prof.', 'professor',
  'mr', 'mr.', 'mrs', 'mrs.', 'ms', 'ms.', 'miss', 'mx', 'mx.',
  'sr', 'sr.', 'sister', 'sir', 'dame', 'rev', 'rev.',
]);

export function initials(name, fallback = '?') {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .filter((p) => !HONORIFICS.has(p.toLowerCase()));

  if (!parts.length) return fallback;

  const chars = parts.length === 1
    ? [...parts[0]].slice(0, 2)
    : [[...parts[0]][0], [...parts[parts.length - 1]][0]];

  return chars.filter(Boolean).join('').toUpperCase() || fallback;
}

/** Deterministic avatar colour so the same person is the same colour everywhere. */
const AVATAR_PALETTE = [
  '#1F5D52', '#3568A6', '#7B4EA6', '#9B4A3A', '#B8892F',
  '#2E6C5F', '#1A4A7A', '#5A3A7A', '#7A3A2A', '#8A6A20',
];

export function colourFor(seed) {
  const s = String(seed || '');
  let hash = 0;
  for (let i = 0; i < s.length; i += 1) {
    hash = (hash * 31 + s.charCodeAt(i)) | 0;
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
}

export { AVATAR_PALETTE };

/** "Dr. Amara Rivera" from profile parts, skipping anything missing. */
export function fullName(profile) {
  if (!profile) return '';
  if (profile.displayName) return profile.displayName;
  return [profile.title, profile.firstName, profile.lastName].filter(Boolean).join(' ');
}

/**
 * What to call someone, given only their display name.
 *
 * displayName is assembled as title + first + last, so the naive
 * `split(' ')[0]` returns the honorific: "Replying to Mrs", "Block Dr".
 * Skipping a leading title gets the actual given name.
 *
 * Falls back to the whole string rather than to nothing — a single-word name,
 * or a title we do not recognise, should still address the person by something.
 */
export function givenName(name, fallback = '') {
  const parts = String(name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return fallback;
  if (parts.length > 1 && HONORIFICS.has(parts[0].toLowerCase())) return parts[1];
  return parts[0];
}

/**
 * The lowercase words a profile can be found by.
 *
 * Firestore has no text index, so "find someone by their surname" has to be an
 * array-contains against tokens we store ourselves. Each part of the name plus
 * the handle, deduplicated, honorifics dropped — searching "mrs" should not
 * return every married woman on the platform.
 *
 * Capped at 12 because array-contains costs an index entry per element and a
 * name with a dozen parts is already pathological.
 */
export function searchTokens(profile) {
  if (!profile) return [];

  const raw = [
    profile.displayName,
    profile.firstName,
    profile.lastName,
    profile.usernameLower || profile.username,
  ].filter(Boolean).join(' ');

  const tokens = raw
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length >= 2 && !HONORIFICS.has(t));

  return [...new Set(tokens)].slice(0, 12);
}

/**
 * The subtitle line under a name: role · location.
 *
 * Specialty is NOT here and must not be added back. It is an input to the
 * briefing engine, not a credential — see authorSnapshot in usePosts.js.
 */
export function credentialLine(post) {
  if (!post) return '';
  // Role only. Location stopped being shown on posts on 3 October.
  return [
    post.showJobTitle !== false && post.authorJobTitle,
  ].filter(Boolean).join(' · ');
}

/** Bytes → "2.4 MB", for upload errors that actually tell you the size. */
export function fileSize(bytes) {
  const b = Number(bytes) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}
